import { useEffect } from 'react';

const GRIP_PX = 24;
const PAD_SELECTOR = 'canvas[data-signature-pad], .react-form-builder-form .form-group canvas';
const INTERACTIVE_SELECTOR =
  'canvas, button, a, input, select, textarea, label, .clear-signature, [role="button"], [role="link"], [role="checkbox"], [role="radio"], [role="switch"], [role="tab"], [role="menuitem"], [role="option"], [role="slider"], [role="combobox"]';

let listeners = 0;

const contains = (rect, x, y, margin = 0) =>
  x >= rect.left - margin &&
  x <= rect.right + margin &&
  y >= rect.top - margin &&
  y <= rect.bottom + margin;

/**
 * The pad whose grip margin (but not the pad itself) holds the point. A touch inside the pad
 * that didn't land on the canvas hit something drawn over it, which keeps its own tap.
 *
 * @param {number} x
 * @param {number} y
 * @param {Element[]} pads
 */
export const findEdgePad = (x, y, pads) =>
  pads.find((pad) => {
    const rect = pad.getBoundingClientRect();
    return rect.width > 0 && contains(rect, x, y, GRIP_PX) && !contains(rect, x, y);
  }) || null;

// WebKit can't construct Touch objects, so the stroke is replayed as the mouse events the pad
// also listens for (mousedown/mousemove on the canvas, mouseup bubbling to the document).
const relay = (type, pad, { clientX, clientY }) =>
  pad.dispatchEvent(
    new MouseEvent(type, {
      bubbles: true,
      cancelable: true,
      button: 0,
      buttons: type === 'mouseup' ? 0 : 1,
      clientX,
      clientY
    })
  );

// Touch events stay with the element the finger first landed on, so a stroke that starts just
// outside a pad never reaches its canvas. Replay that stroke onto the pad once it enters it.
const handleTouchStart = (event) => {
  if (event.touches.length !== 1) return;
  const { target } = event;
  if (!(target instanceof Element) || target.closest(INTERACTIVE_SELECTOR)) return;

  const [touch] = event.touches;
  const pad = findEdgePad(touch.clientX, touch.clientY, [
    ...document.querySelectorAll(PAD_SELECTOR)
  ]);
  if (!pad) return;

  event.preventDefault();
  let isDrawing = false;

  const handleMove = (moveEvent) => {
    moveEvent.preventDefault();
    const point = moveEvent.changedTouches[0];
    if (isDrawing) {
      relay('mousemove', pad, point);
      return;
    }
    if (!contains(pad.getBoundingClientRect(), point.clientX, point.clientY)) return;
    isDrawing = true;
    relay('mousedown', pad, point);
  };
  const handleEnd = (endEvent) => {
    if (isDrawing) relay('mouseup', pad, endEvent.changedTouches[0]);
    target.removeEventListener('touchmove', handleMove);
    target.removeEventListener('touchend', handleEnd);
    target.removeEventListener('touchcancel', handleEnd);
  };
  target.addEventListener('touchmove', handleMove, { passive: false });
  target.addEventListener('touchend', handleEnd);
  target.addEventListener('touchcancel', handleEnd);
};

/** Lets a finger stroke that starts up to GRIP_PX outside a signature pad still draw. */
export const useSignatureEdgeGrip = () => {
  useEffect(() => {
    if (listeners === 0) {
      document.addEventListener('touchstart', handleTouchStart, { capture: true, passive: false });
    }
    listeners += 1;

    return () => {
      listeners -= 1;
      if (listeners === 0) {
        document.removeEventListener('touchstart', handleTouchStart, { capture: true });
      }
    };
  }, []);
};
